import { useCallback, useEffect, useMemo, useState } from "react";
import { conversationAnalyticsManager } from "../../data/managers/ConversationAnalyticsManager";
import { CreateConversationAnalyticsJobParams } from "../../data/api/ConversationAnalyticsApi";
import {
  ConversationAnalyticsConfig,
  ConversationAnalyticsJob,
  ConversationAnalyticsOverview,
  ConversationCustomerProfile,
  ConversationServiceAnalysis,
} from "../../data/models/Analytics/ConversationAnalyticsModels";

const toDateInput = (date: Date) => date.toISOString().slice(0, 10);

const getMonthRange = (monthOffset: number) => {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth() + monthOffset;
  const rangeStart = new Date(Date.UTC(year, month, 1));
  const rangeEnd =
    monthOffset === 0
      ? now
      : new Date(Date.UTC(rangeStart.getUTCFullYear(), rangeStart.getUTCMonth() + 1, 0));

  return {
    from: toDateInput(rangeStart),
    to: toDateInput(rangeEnd),
  };
};

export function useConversationAnalytics(enabled = true) {
  const currentMonthRange = useMemo(() => getMonthRange(0), []);
  const previousMonthRange = useMemo(() => getMonthRange(-1), []);
  const [dateFrom, setDateFrom] = useState(currentMonthRange.from);
  const [dateTo, setDateTo] = useState(currentMonthRange.to);
  const [showCurrent, setShowCurrent] = useState(true);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<ConversationAnalyticsConfig>();
  const [overview, setOverview] = useState<ConversationAnalyticsOverview>();
  const [services, setServices] = useState<ConversationServiceAnalysis[]>([]);
  const [customers, setCustomers] = useState<ConversationCustomerProfile[]>([]);
  const [customerTimeline, setCustomerTimeline] = useState<
    ConversationServiceAnalysis[]
  >([]);
  const [jobs, setJobs] = useState<ConversationAnalyticsJob[]>([]);
  const [servicesTotal, setServicesTotal] = useState(0);
  const [customersTotal, setCustomersTotal] = useState(0);
  const [timelineLoading, setTimelineLoading] = useState(false);

  const rangeParams = useMemo(
    () => ({
      dateFrom,
      dateTo,
    }),
    [dateFrom, dateTo]
  );

  useEffect(() => {
    const nextRange = showCurrent ? currentMonthRange : previousMonthRange;
    setDateFrom(nextRange.from);
    setDateTo(nextRange.to);
  }, [currentMonthRange, previousMonthRange, showCurrent]);

  const refresh = useCallback(async () => {
    if (!enabled) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [nextConfig, nextOverview, nextServices, nextCustomers, nextJobs] =
        await Promise.all([
          conversationAnalyticsManager.getConfig(),
          conversationAnalyticsManager.getOverview(rangeParams),
          conversationAnalyticsManager.getServices({
            ...rangeParams,
            page: 0,
            pageSize: 1000,
          }),
          conversationAnalyticsManager.getCustomers({
            ...rangeParams,
            page: 0,
            pageSize: 1000,
          }),
          conversationAnalyticsManager.getJobs(),
        ]);
      setConfig(nextConfig);
      setOverview(nextOverview);
      setServices(nextServices?.items || []);
      setServicesTotal(nextServices?.total || 0);
      setCustomers(nextCustomers?.items || []);
      setCustomersTotal(nextCustomers?.total || 0);
      setJobs(nextJobs);
    } catch (err: any) {
      setError(err?.message || "No se pudieron cargar las analiticas.");
    } finally {
      setLoading(false);
    }
  }, [enabled, rangeParams]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    void refresh();
  }, [enabled, refresh]);

  const createJob = useCallback(
    async (params: CreateConversationAnalyticsJobParams) => {
      setActionLoading(true);
      setError(null);
      try {
        await conversationAnalyticsManager.createJob(params);
        await refresh();
        return true;
      } catch (err: any) {
        setError(err?.message || "No se pudo crear el job.");
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    [refresh]
  );

  const runJob = useCallback(
    async (jobId: string, maxBatches?: number) => {
      setActionLoading(true);
      setError(null);
      try {
        await conversationAnalyticsManager.runJob(jobId, maxBatches);
        await refresh();
        return true;
      } catch (err: any) {
        setError(err?.message || "No se pudo ejecutar el job.");
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    [refresh]
  );

  const saveConfig = useCallback(
    async (nextConfig: Partial<ConversationAnalyticsConfig>) => {
      setActionLoading(true);
      setError(null);
      try {
        await conversationAnalyticsManager.updateConfig(nextConfig);
        await refresh();
        return true;
      } catch (err: any) {
        setError(err?.message || "No se pudo guardar la configuración.");
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    [refresh]
  );

  const loadCustomerTimeline = useCallback(
    async (phoneNumber: string) => {
      setTimelineLoading(true);
      setError(null);
      try {
        const timeline =
          await conversationAnalyticsManager.getCustomerTimeline(phoneNumber, {
            dateFrom: rangeParams.dateFrom,
            dateTo: rangeParams.dateTo,
          });
        setCustomerTimeline(timeline);
        return timeline;
      } catch (err: any) {
        setError(err?.message || "No se pudo cargar el timeline del cliente.");
        setCustomerTimeline([]);
        return [];
      } finally {
        setTimelineLoading(false);
      }
    },
    [rangeParams],
  );

  return {
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    showCurrent,
    setShowCurrent,
    loading,
    actionLoading,
    error,
    config,
    overview,
    services,
    servicesTotal,
    customers,
    customersTotal,
    customerTimeline,
    timelineLoading,
    jobs,
    refresh,
    createJob,
    runJob,
    saveConfig,
    loadCustomerTimeline,
  };
}
